import { CMD } from '@/pb/protocol_cmd_pb'
import {
  SignalingInviteRelayNty, SignalingMemberStatusChangedNty, SignalingQueryReq,
  SignalingQueryRsp, SignalingSessionStatusChangedNty, SignalingUserJoinedNty,
  SignalingJoinRsp
} from '@/pb/protocol_signaling_pb'
import { MediaStatusChangedNty, MediaControlRsp } from '@/pb/protocol_media_control_pb'
import { Packet } from '@/pb/header_entity_pb'
import { Entity } from '@/pb/entity_pb'
import { ESig, SigSessionDetail } from '@/pb/signaling_param_pb'
import { UserLoginRsp, UserKeepLiveRsp } from '@/pb/protocol_login_pb'
import { IMMsgRpcRsp, IMMsgRelayNty, IMOffLineMsgsNty, BatchIMMsgRelayRcp, IMMsgRelayRcp } from '@/pb/protocol_msg_pb'
import { EMsg } from '@/pb/msg_entity_pb'
import { MediaEngineConfigInfo } from '@/pb/mediainfo_pb'
import Global from '@/global'
import { fromPairs, map, reverse } from 'ramda'
import router from '@/router'
import { CACHE_KEY, NOTIFY_TYPE, PAGE_NAME } from './utils/constant'
import { queryConference } from '@/api/coc'
import { Gate } from '@/pb/gate_pb'
import { CSync } from '@/pb/sync_pb'
import { MessageIds } from '@/pb/message_pb'
import { ConfRoleRequestNty, ConfRoleRequestReply } from '@/pb/protocol_conference_pb'
import { message, Modal } from 'ant-design-vue'
import { format } from 'date-fns'
import Observer from '@/utils/observer'
import Vue from 'vue'
import { EDeviceType } from '@/pb/status_entity_pb'
import store from '@/store'
import { MICROPHONEAUTHSTATES } from '@/utils/microPhoneAuthCheck'
import i18n from '@/i18n'
import rtc from '@/rtccomm'
import { UserLastStatusBatchNty } from '@/pb/protocol_status_pb'

const MUTESTATE = {
  MUTE: 1,
  NOTMUTE: 0
}

let traceIdSequence = 0
let transactionIdSequence = 100

const cmdFromCode = fromPairs(map(reverse, Object.entries(CMD)))

function toHump(name) {
  return ('_' + name).toLowerCase().replace(/_(\w)/g, function(all, letter){
    return letter.toUpperCase()
  })
}

function generateRequestId () {
  return new Date().getTime() % Global.INT_32_MAX_VALUE
}

// 判断成员是否在房间内
function checkMemberInRoom(member) {
  return (
    member.status >= ESig.MemberStatusType.INROOM_INIT &&
    member.status <= ESig.MemberStatusType.INROOM_MAX
  )
}

// 眼镜 GLASSES H100 CIMO WU_KONG
function isGlassDevice (devicetype) {
  return devicetype === EDeviceType.GLASSES || devicetype === EDeviceType.H100 || devicetype === EDeviceType.CIMO || devicetype === EDeviceType.WU_KONG
}

// 去除冻屏图片url的域名前缀
function removeUrlHost(url) {
  return url.replace(/^.*\/\/[^/]+/, '')
}

// 转换mqtt返回的成员状态
function transformMember (members) {
  return members.map(member => ({
    memberId: member.memberid,
    role: member.rolemask,
    deviceType: member.devicetype,
    isGlassDevice: isGlassDevice(member.devicetype),
    ...transformMemberStatus(member),
    id: member.contact.id,
    name: member.contact.name,
    appId: member.contact.appid,
    avatarUrl: member.contact.avatarurl,
    corpName: member.contact.corpname,
    rolesList: member.contact.rolesList,
    isHost: member.contact.rolesList.some(role => +role === Global.MEMBER_ROLE.ROLE_HOST),
    isAnnotator: member.contact.rolesList.some(role => +role === Global.MEMBER_ROLE.ROLE_SPECIALIST),
    microphoneOn: member.controlsList.find(control => +control.op === ESig.ControlOption.MICROPHONE)?.onoff ?? false,
    cameraOn: member.controlsList.find(control => +control.op === ESig.ControlOption.CAMERA)?.onoff ?? false,
    speakerOn: member.controlsList.find(control => +control.op === ESig.ControlOption.SPEAKER)?.onoff ?? false,
    freezeOn: member.controlsList.find(control => +control.op === ESig.ControlOption.ONESELF_FREEZE)?.onoff ?? false,
    whiteBoardOn: member.controlsList.find(control => +control.op === ESig.ControlOption.ONESELF_WHITEBOARD)?.onoff ?? false,
    freezeImage: removeUrlHost(member.controlsList.find(control => +control.op === ESig.ControlOption.ONESELF_FREEZE)?.svalue ?? ''),
    screenSharingOn: member.controlsList.find(control => +control.op === ESig.ControlOption.ONESELF_SCREEN_SHARING)?.onoff ?? false,
    realtimeMarkOn: member.controlsList.find(control => +control.op === ESig.ControlOption.ONESELF_REALTIME_ANNOTATION)?.onoff ?? false,
    realtimeMarkTargetUserId: member.controlsList.find(control => +control.op === ESig.ControlOption.ONESELF_REALTIME_ANNOTATION)?.svalue ?? '',
    isPlayBigVideo: false,
    rtcUser: {},
    slamMarkOn: member.controlsList.find(control => +control.op === ESig.ControlOption.ONESELF_SLAM_ANNOTATION)?.onoff ?? false,
    slamMarkTargetUserId: member.controlsList.find(control => +control.op === ESig.ControlOption.ONESELF_SLAM_ANNOTATION)?.svalue ?? '',
  }))
}

// 转换用户的媒体操作权限
function transformMemberControl (controlList) {
  const result = {}
  const microphoneOn = controlList.find(control => +control.op === ESig.ControlOption.MICROPHONE)?.onoff
  const cameraOn = controlList.find(control => +control.op === ESig.ControlOption.CAMERA)?.onoff
  const speakerOn = controlList.find(control => +control.op === ESig.ControlOption.SPEAKER)?.onoff
  const freezeOn = controlList.find(control => +control.op === ESig.ControlOption.ONESELF_FREEZE)?.onoff
  const whiteBoardOn = controlList.find(control => +control.op === ESig.ControlOption.ONESELF_WHITEBOARD)?.onoff
  const screenSharingOn = controlList.find(control => +control.op === ESig.ControlOption.ONESELF_SCREEN_SHARING)?.onoff
  const realtimeMarkOn = controlList.find(control => +control.op === ESig.ControlOption.ONESELF_REALTIME_ANNOTATION)?.onoff
  const slamMarkOn = controlList.find(control => +control.op === ESig.ControlOption.ONESELF_SLAM_ANNOTATION)?.onoff
  if (microphoneOn !== undefined) result.microphoneOn = microphoneOn
  if (cameraOn !== undefined) {
    result.cameraOn = cameraOn
    // 如果摄像头已经关闭，则把对应的大屏播放也关闭
    if (cameraOn === false) result.isPlayBigVideo = false
  }
  if (speakerOn !== undefined) result.speakerOn = speakerOn
  if (freezeOn !== undefined) {
    result.freezeOn = freezeOn
    result.freezeImage = removeUrlHost(controlList.find(control => +control.op === ESig.ControlOption.ONESELF_FREEZE)?.svalue || '')
  }
  if (whiteBoardOn !== undefined) {
    result.whiteBoardOn = whiteBoardOn
  }
  if (screenSharingOn !== undefined) result.screenSharingOn = screenSharingOn
  if (realtimeMarkOn !== undefined) {
    result.realtimeMarkOn = realtimeMarkOn
    result.realtimeMarkTargetUserId = controlList.find(control => +control.op === ESig.ControlOption.ONESELF_REALTIME_ANNOTATION)?.svalue || ''
  }
  if (slamMarkOn !== undefined) {
    result.slamMarkOn = slamMarkOn
    result.slamMarkTargetUserId = controlList.find(control => +control.op === ESig.ControlOption.ONESELF_SLAM_ANNOTATION)?.svalue || ''
  }
  return result
}

// 转换用户的身份权限
function transformMemberRoles (rolesList) {
  return {
    rolesList,
    isHost: rolesList.some(role => +role === Global.MEMBER_ROLE.ROLE_HOST),
    isAnnotator: rolesList.some(role => +role === Global.MEMBER_ROLE.ROLE_SPECIALIST)
  }
}

// 转换用户状态，使其语义化
function transformMemberStatus (member) {
  return {
    status: member.status,
    inRoom: checkMemberInRoom(member),
    isWaiting: [ESig.MemberStatusType.PENDING_WAIT_INVITE_ACK, ESig.MemberStatusType.PENDING_WAIT_INVITE_ACCEPT].includes(member.status), // 等待接听
    isLeft: member.status === ESig.MemberStatusType.OUTROOM_USER_LEAVE, // 已离开
    isTimeout: member.status === ESig.MemberStatusType.OUTROOM_USER_TIMEOUT, // 已超时
    isOffline: member.status === ESig.MemberStatusType.OUTROOM_USER_DROPLINE, // 已离线
    isBusy: member.status === ESig.MemberStatusType.OUTROOM_USER_BUSY, // 对方忙
    isRefuse: member.status === ESig.MemberStatusType.OUTROOM_USER_REFUSE, // 已拒绝
    isKicked: member.status === ESig.MemberStatusType.OUTROOM_USER_KICKOUT, // 被踢除
  }
}

// 检测web端暂不支持的功能
// function checkUnsupported (context, controlList) {
//   const unsupported = [
//     ESig.ControlOption.ONESELF_WHITEBOARD, // 白板
//     // ESig.ControlOption.ONESELF_SLAM_ANNOTATION // SLAM
//   ]
//   if (controlList.some(control => unsupported.includes(control.op) && control.onoff)) {
//     context.commit('setShowUnsupportedNotify')
//   }
// }

// 该房间是否可用
function isRoomAvailable (context, room) {
  const roomInfo = room.getInfo()
  // 检测房间状态
  if (!roomInfo || roomInfo.getStatus() === ESig.SessionStatusType.SESSION_TERMINATED) return Promise.reject(new Error(i18n.tc('mqtt.CollaborationClosed')))

  // 检测rtc配置
  const extInfo = roomInfo.getExtinfo()
  const mediaConfig = MediaEngineConfigInfo.deserializeBinary(new TextEncoder().encode(extInfo)).toObject()
  if (mediaConfig.mediatype !== Global.MediaSource.MediaSAgora && mediaConfig.mediatype !== Global.MediaSource.MediaSLeiartc) {
    return Promise.reject(new Error(i18n.tc('mqtt.RejectedCollaborationOfNotRTC'))) // 当前协作的RTC类型不是声网或LeiaRTC，已取消本次协作
  }

  const membersList = room.toObject().membersList
  const conferenceId = roomInfo.getSrcid()
  if (membersList && membersList.length > 0) {
    context.commit('saveSessionMembers', transformMember(membersList))
  }
  context.commit('saveCurrentSession', {
    status: roomInfo.getStatus(),
    srcId: conferenceId,
    roomId: roomInfo.getRoomid(),
    roomKey: roomInfo.getRoomkey(),
    roomName: roomInfo.getName(),
    inviterAvatar: membersList.find(member => +member.memberid === +context.state.userInfo.id)?.contact?.avatarurl
  })
  context.dispatch('initRtcClient', { ...mediaConfig, isHost: +roomInfo.getCreatorid() === +context.state.userInfo.id })
  context.commit('switchStatus', MachineStatus.SessionEstablished)

  // patch 没有允许或者拒绝授权，关闭麦克风
  const localUser = membersList.find(member => +member.memberid === +context.state.userInfo.id)
  if (localUser && localUser.controlsList){
    const cs = transformMemberControl(localUser.controlsList)
    if (cs.microphoneOn && Global.MICROPHONEAUTHSTATE !== MICROPHONEAUTHSTATES.ALLOW) {
      context.dispatch('controlMicrophone', false)
    }
  }
  return context.dispatch('startSessionKeepAlive')
}

/**
 * 返回 Message constructor `opt-data` 数组格式，次序重要
 * @returns {(number|*|number|number|string)[]} as himpb.Base.PacketHeader
 */
function generateBasePacketHeader(context) {
  const timestamp = Date.now()
  return [
    /* timestamp: */ timestamp, // c++@System2.h:GetSystemTime()
    /* userid: */ context.rootState.userInfo.id,
    /* usertype: */ 0,
    /* appid: */ context.rootState.userInfo.corpId,
    /* apptype: */ 0,
    /* devicetype: */ Global.HIMDeviceType.kHIMDeviceType_Web,
    /* traceid: */ `${timestamp}${++traceIdSequence}`, // c++@him_client_engine.cpp:CHIMCliEngine::setReqBase:snprintf...
    /* transid: */ `${++transactionIdSequence}`, // c++@him_utils.cpp:GenTransID()
  ]
}

// 处理通知
function handleImMsgNty (context, imData) {
  const brief/* TMsgBrief */ = imData.getBrief()
  const body = imData.getBody()

  const briefMode = brief.getMode()
  const briefType = brief.getType()

  let msgData

  switch (brief.getEncodetype()) {
    case EMsg.EncodeType.JSON_FORMAT_ENCODING: // 应该只需要要支持这个格式
      msgData = JSON.parse(Global.uint8ArrayToString(body)) // 如果异常，则自动报错终止后面
      break

    case EMsg.EncodeType.PROBUF_FORMAT_ENCODING:
      break
  }

  if (briefMode === EMsg.MsgMode.APP_NOTIFY && briefType === EMsg.MsgType.MSG_TYPE_CUSTOM) {
    // 添加外部联系人请求
    // 1收到邀请 3拒绝邀请
    if (msgData.type !== undefined) {
      // 被邀请加好友
      context.dispatch('notification/unshiftUnreadNotify', {
        notifyType: NOTIFY_TYPE.IM_MSG_RELAY_NTY,
        data: msgData
      })
    }
    return
  }

  if (briefMode === EMsg.MsgMode.APP_CONF && briefType >= EMsg.MsgType.MSG_OP_CONF_INVITE_NTY && briefType <= EMsg.MsgType.MSG_OP_CONF_STOP_NTY) {
    const isCancelCoc = briefType === EMsg.MsgType.MSG_OP_CONF_CANCEL_NTY // 取消会议
    const isKOUser = briefType === EMsg.MsgType.MSG_OP_CONF_REMOVE_MEMBER_NTY // 踢人
    // const isNewFile = briefType === EMsg.MsgType.MSG_OP_CONF_ADD_FILES_NTY // 更新文件，else已经处理了
    const conferenceId = msgData.conferenceId

    if (isCancelCoc || isKOUser) {
      context.commit('coc/removeCoc', conferenceId)
      context.dispatch('notification/unshiftUnreadNotify', {
        notifyType: NOTIFY_TYPE.COC_NTY,
        data: { ...msgData, type: briefType }
      })
    } else {
      context.dispatch('coc/refreshCoc', conferenceId).then(({ confirmStatus, attendStatus, hostIsMe }) => {
        context.dispatch('notification/unshiftUnreadNotify', {
          notifyType: NOTIFY_TYPE.COC_NTY,
          data: { ...msgData, confirmStatus, attendStatus, hostIsMe, type: briefType }
        })
      })
    }
  }
}

// 发送已收到消息的回执信令
function replyImMsg (context, brief) {
  const relay = new IMMsgRelayRcp([
    /* base: */ [
      /* header: */generateBasePacketHeader(context),
      /* code: */0,
      /* errMsg: */''
    ]
  ])
  brief.setStatus(EMsg.MsgStatus.MSG_STATUS_UNREAD)
  relay.setBrief(brief)
  relay.cmd = CMD.IM_MSG_RELAY_RCP
  doPublish(context, relay)
}

/**
 * 重要协议流程参考：
 * http://wiki.hiscene.net/pages/viewpage.action?pageId=2591718 详询杨赛程、柳啸雷
 */
function onMqttMessage(context, topic, packet) {
  const cmd = packet.getCmd()
  const data = packet.getData()

  let deserializedData

  switch (+cmd) {
    case CMD.USER_LAST_STATUS_BATCH_NTY:
    case CMD.USER_LAST_STATUS_NTY:
      if (topic === context.rootState.serverProvidedConfig.MQTTCfg.SubUserStatusTopic) {
        deserializedData = UserLastStatusBatchNty.deserializeBinary(data).toObject()
        context.dispatch('collaborator/setOnlineUsers', deserializedData.statusList)
      }
      return
  }

  console.log(`message received： ${cmd}  %c${cmdFromCode[cmd]}  %c${toHump(cmdFromCode[cmd])}`, 'color: blue', 'color: red')
  if (context.state.status !== MachineStatus.UnloggedIn) { // 只要登录就需要处理的消息
    switch (cmd) {
      case CMD.IM_MSG_RELAY_NTY: {
        deserializedData = IMMsgRelayNty.deserializeBinary(data)
        const msgEntity/* TMsgEntity */ = deserializedData.getMsg()
        // 处理通知
        handleImMsgNty(context, msgEntity)
        // 发送回执
        replyImMsg(context, msgEntity.getBrief())
        break
      }
      case CMD.USER_LOGIN_ON_OTHER_DEVICE_NTY:{ // 处理被踢消息
        const isActived = Vue.ls.get(CACHE_KEY.NAV_TAG_COUNTER) === 1
        context.dispatch('mqttLogout')
        context.dispatch('logoutReset', isActived)
        if (!isActived) {
          message.warning('一个浏览器仅支持登录一个账号')
        } else {
          Modal.confirm({
            content: i18n.tc('mqtt.AccountLoginOfAntherDevice'),
            width: '460px',
            centered: true,
            class: 'confirm-exit',
            closable: true,
            okText: i18n.tc('common.Ok'),
            cancelText: i18n.tc('common.Cancel')
          })
        }
        break
      }
      case CMD.USER_KEEPLIVE_RSP: { // 收到会话心跳响应
        console.log('A USER_KEEPLIVE_RSP message', format(new Date(), 'HH:mm:ss'))
        const rsp = UserKeepLiveRsp.deserializeBinary(data).toObject()
        if (rsp?.base?.code !== 0) {
          console.log('mqtt已退出登录', format(new Date(), 'HH:mm:ss'))
          let offlineMsg = i18n.tc('mqtt.ReconnectingIMService') + '...'
          context.commit('switchStatus', MachineStatus.UnloggedIn)
          context.commit('stopUserHeartBeater')
          context.commit('setMqttOffline', true)
          if (router.currentRoute.name === PAGE_NAME.Talking) {
            offlineMsg = i18n.tc('mqtt.LeaveCollaborationOfDisconnected') + offlineMsg
            context.dispatch('leaveRtcRoom')
          }
          message.loading({
            content: h => {
              return h('div', {
                style: {
                  display: 'inline-block'
                }
              }, [
                offlineMsg,
                '（' + i18n.tc('mqtt.AlwaysConnected'),
                h('span', {
                  style: {
                    color: '#236CFF',
                    textDecoration: 'underline',
                    cursor: 'pointer'
                  },
                  on: {
                    click: () => {
                      context.dispatch('mqttLogin')
                    }
                  }
                },
                [i18n.tc('mqtt.PleaseClickHere')]),
                '）'
              ])
            },
            key: 'offlineMsg',
            duration: 0
          })
        }
        break
      }
      case CMD.SIGNALING_SESSION_STATUS_CHANGED_NTY: { // 收到已经接受或参与的协作状态变化
        const conferenceId = SignalingSessionStatusChangedNty.deserializeBinary(data).getSrcid()
        context.dispatch('coc/refreshCoc', conferenceId)
        break
      }
    }
  }
  switch (context.state.status) {
    case MachineStatus.UnloggedIn:
      switch (cmd) {
        case CMD.USER_LOGIN_RSP:
          // 解析登录返回的数据
          deserializedData = UserLoginRsp.deserializeBinary(data).toObject()
          console.log('登录结果返回：  ', format(new Date(), 'HH:mm:ss'))
          // 登录成功
          if (deserializedData?.base?.code === 0) {
            console.info('mqtt登录成功')
            Observer.$emit('mqtt-logon')
            context.dispatch('startUserKeepAlive')
            // 如果是从离线状态恢复登录
            if (context.state.isMqttOffline) {
              context.commit('setMqttOffline', false)
              message.success({
                content: i18n.tc('mqtt.ServiceConnectionSucc'),
                key: 'offlineMsg',
                duration: 3
              })
            }
            if (router.currentRoute.name === PAGE_NAME.Talking) {
              // 刷新页面引起的
              const conferenceId = router.currentRoute.params.conferenceId
              queryConference(conferenceId)
                .then(conferenceInfo => {
                  const queryRequest = new SignalingQueryReq([
                    /* base: */ [
                      /* header: */generateBasePacketHeader(context),
                    ],
                    /* roomid: */ conferenceInfo.roomId,
                    /* roomkey: */ conferenceInfo.roomKey
                  ])
                  queryRequest.cmd = CMD.SIGNALING_QUERY_REQ
                  doPublish(context, queryRequest)
                  context.commit('switchStatus', MachineStatus.Rejoining)
                })
            } else {
              context.commit('switchStatus', MachineStatus.Idle)
            }
          } else {
            console.log('mqtt登录失败')
            message.error(i18n.tc('mqtt.UserInfoInvalid'))
            context.dispatch('logoutReset')
          }
          break
      }
      break
    case MachineStatus.Rejoining:
      switch (cmd){
        case CMD.SIGNALING_QUERY_RSP: {
          const response = SignalingQueryRsp.deserializeBinary(data)
          // 如果房间异常，直接退出通话界面
          isRoomAvailable(context, response).then(() => {
            // 如果缓存中有冻屏线条数据，则取出
            const lines = Vue.ss.get(CACHE_KEY.MEMBER_LINES) || {}
            const conferenceId = response.getInfo().getSrcid()
            if (lines[conferenceId]) {
              context.commit('setLinesData', lines[conferenceId])
            }
            // 如果缓存中有白板图片，则取出
            const whiteBoardImages = Vue.ss.get(CACHE_KEY.MEMBER_WHITE_BOARD_IMAGE) || ''
            if (whiteBoardImages[conferenceId]) {
              context.dispatch('setWhiteBoardImage', whiteBoardImages[conferenceId])
            }
            // 如果缓存中有白板线条数据，则取出
            const whiteBoardLinesData = Vue.ss.get(CACHE_KEY.MEMBER_WHITE_BOARD_LINES) || {}
            if (whiteBoardLinesData[conferenceId]) {
              context.commit('setWhiteBoardLinesData', whiteBoardLinesData[conferenceId])
            }
          }).catch(err => {
            message.error(err.message)
            context.dispatch('leaveRtcRoom')
          })
          break
        }
      }
      break
    case MachineStatus.Calling:
      switch (cmd) {
        case CMD.SIGNALING_QUERY_RSP: { // 发出邀请后主动查询房间及成员的全量信息
          const response = SignalingQueryRsp.deserializeBinary(data)
          if (response.getRoomid() === context.state.currentSession.roomId) {
            // 如果房间可用，则进入通话界面
            isRoomAvailable(context, response).then(() => {
              const conferenceId = response.getInfo().getSrcid()
              router.push({
                path: `/talking/${conferenceId}`,
                query: {
                  from: router.currentRoute.name
                }
              })
            }).catch(err => {
              message.error(err.message)
              context.dispatch('leaveRtcRoom')
            })
          }
          break
        }
        case CMD.SIGNALING_INVITE_RSP: {
          console.log('收到邀请回复')
          break
        }
      }
      break
    case MachineStatus.SessionEstablished:
      switch (cmd) {
        case CMD.SIGNALING_SESSION_STATUS_CHANGED_NTY: { // 通话中的房间状态通知
          const nty = SignalingSessionStatusChangedNty.deserializeBinary(data)
          if (nty.getRoomid() === context.state.currentSession.roomId) {
            // 房间状态变更为终止，即主持人终止了此次会话
            if (nty.getStatus() === ESig.SessionStatusType.SESSION_TERMINATED) {
              console.log('房间被关闭')
              context.dispatch('leaveRtcRoom')
            }
          }
          break
        }
        case CMD.SIGNALING_KEEPLIVE_RSP : // 收到会话心跳响应
        {
          console.log('A SIGNALING_KEEPLIVE_RSP message')
          break
        }
        case CMD.SIGNALING_INVITE_OFFLINE_RELAY_NTY: // 已处于通话中，又收到其他用户的离线会话邀请
        case CMD.SIGNALING_INVITE_RELAY_NTY: // 已处于通话中，又收到其他用户的在线会话邀请
        {
          const nty = SignalingInviteRelayNty.deserializeBinary(data)
          const roomInfo = {
            roomId: nty.getInfo().getRoomid(),
            roomKey: nty.getInfo().getRoomkey(),
            inviterId: nty.getInviterid()
          }
          // 为什么不能直接发送拒绝的请求
          // 因为收到服务器的邀请，本地必须要先发送确认邀请
          // 等服务器收到确认信息后，本地才能发送拒绝请求
          if (roomInfo.roomId !== context.state.currentSession.roomId) {
            // 确认邀请
            context.dispatch('acknowledgeInvitation', roomInfo)
            // 存储邀请信息
            context.commit('saveOtherInvitations', roomInfo)
          }
          break
        }
        case CMD.SIGNALING_USER_JOINED_NTY : // 会话中，有新的member进来了，此消息携带新member的详细信息
        {
          const nty = SignalingUserJoinedNty.deserializeBinary(data).toObject()
          if (nty.roomid === context.state.currentSession.roomId) {
            context.commit('addSessionMember', ...transformMember(nty.membersList))
          }
          break
        }
        case CMD.SIGNALING_MEMBER_STATUS_CHANGED_NTY : // 会话中，收到member状态变更
        {
          const nty = SignalingMemberStatusChangedNty.deserializeBinary(data).toObject()
          if (nty.roomid === context.state.currentSession.roomId) {
            switch (nty.status) {
              default:
              {
                const targetUser = context.state.sessionMembers.find(member => +member.id === +nty.memberid)
                if (targetUser) {
                  // 如果当前用户被踢出房间，则离开
                  if (+targetUser.id === +context.state.userInfo.id && nty.status === ESig.MemberStatusType.OUTROOM_USER_KICKOUT) {
                    context.dispatch('leaveRtcRoom')
                    message.warn(i18n.tc('mqtt.BeenRemoved'))
                    return
                  }
                  const controls = transformMemberControl(nty.controlsList)
                  context.commit('updateSessionMembers', {
                    ...targetUser,
                    deviceType: nty.devicetype,
                    ...transformMemberStatus(nty),
                    ...controls,
                    ...transformMemberRoles(nty.rolesList)
                  })

                  // patch 如果没有第一次点击授权关闭麦克风
                  if (controls.microphoneOn && Global.MICROPHONEAUTHSTATE !== MICROPHONEAUTHSTATES.ALLOW) {
                    context.dispatch('controlMicrophone', false)
                  }
                  // checkUnsupported(context, nty.controlsList)
                }
              }
            }
          } else if (+nty.memberid === +context.state.userInfo.id) {
            const invitation = context.state.receivedOtherInvitations[nty.roomid]
            if (nty.status === ESig.MemberStatusType.PENDING_WAIT_INVITE_ACCEPT && invitation) {
              // 发送拒接请求
              context.dispatch('rejectCallAsBusy', invitation)
              context.commit('deleteOtherInvitations', invitation)
            }
          }
          break
        }
        case CMD.MEDIA_STATUS_CHANGED_NTY : // 会话中，收到member媒体状态变更
        {
          const data = packet.getData()
          const nty = MediaStatusChangedNty.deserializeBinary(data)
          const userId = nty.getOwnerid()
          const targetUser = context.state.sessionMembers.find(member => +member.id === +userId)
          const controlResult = transformMemberControl(nty.toObject().controlsList)
          context.dispatch('updateSessionMemberMedia', {
            ...targetUser,
            ...controlResult
          })
          if (+userId === +context.state.userInfo.id) {
            console.log('被静音了')
            // 如果是当前用户被他人控制媒体开关，则需要本地做对应的rtc操作
            context.dispatch('controlLocalMedia', { ...controlResult, isControlled: true })
          } else {
            // * 其他人员静音通知
            if ('microphoneOn' in controlResult) {
              // store.state.sessionMediaClient.localTracks
              const remoteUsers = store.state.sessionMediaClient.remoteUsers
              remoteUsers.forEach(r => {
                if (+r.uid === +targetUser.id){
                  setTimeout(_ => {
                    console.log(`用户【${targetUser.name}】切换麦克风【${controlResult.microphoneOn ? 'on' : 'off'}】`)
                    console.log(`用户音频轨道【${(r._audioTrack && Object.keys(r._audioTrack)) ? '存在' : '不存在'}】 ${(r._audioTrack && Object.keys(r._audioTrack)) ? r._audio_muted_ ? '当前音频轨道静音【是】，请稍后确认' : '当前音频轨道静音【否】，请稍后确认' : ''}`)
                  }, 0)
                }
              })
            }
          }
          // checkUnsupported(context, nty.toObject().controlsList)
          break
        }
        case CMD.MEDIA_CONTROL_RSP : // 用户自己控制媒体开关的服务端响应
        {
          const data = packet.getData()
          const nty = MediaControlRsp.deserializeBinary(data).toObject()
          if (+nty.base.code !== 0) {
            // 超过4路视频，不开启视频
            // 超过16路音频，回滚音频开启
            if (+nty.base.code === 1028) {
              const targetUser = context.state.sessionMembers.find(member => +member.id === +nty.ownerid)
              let controlResult = nty.controlsList.map(i => {
                // 2是摄像头，1是音频
                if (i.op === 2 || i.op === 1) {
                  return { ...i, onoff: false }
                }
              })
              controlResult = transformMemberControl(controlResult)
              context.commit('updateSessionMembers', { ...targetUser, ...controlResult })
              if (+nty.ownerid === +context.state.userInfo.id) {
                if ('microphoneOn' in controlResult) {
                  // 关闭麦克风，只有麦克风需要回退
                  context.dispatch('controlLocalMedia', { ...controlResult, isControlled: true })
                }
                if ('cameraOn' in controlResult) {
                  // 视频打不开，在信令之后
                  // 需要取消屏幕分享的pending，摄像头和屏幕共享使用一个信令KEY，cameraOn
                  // 如果是屏幕共享pending中，表示开启屏幕共享。屏幕共享不在pending中，则开始摄像头
                  // 故，开不起视频或者共享需要把，共享的pending清除
                  context.commit('setLocalScreenSharingStatus', Global.SCREEN_SHARING_STATUS.off)
                }
              }
            } else if (+nty.base.code === 1911){
              // 用户不在房间
              // 1. 退出媒体引擎
              // 2. 退出页面
              message.warn(nty.base?.errmsg || i18n.tc('mqtt.OperationFailedTryAgain'))
              context.dispatch('leaveRtcRoom')
              return
            }
            message.warn(nty.base?.errmsg || i18n.tc('mqtt.OperationFailedTryAgain'))
            return
          }
          const targetUser = context.state.sessionMembers.find(member => +member.id === +nty.ownerid)
          const controlResult = transformMemberControl(nty.controlsList)
          context.commit('updateSessionMembers', {
            ...targetUser,
            ...controlResult
          })
          if (+nty.ownerid === +context.state.userInfo.id) {
            // patch 关闭麦克风先调用声网在通知mqtt,到这里不再需要调用声网
            if ('microphoneOn' in controlResult) {
              break
            } else if ('cameraOn' in controlResult) {
              rtc.listenerChangeVideoStream(context.state.sessionMediaType, store.state.sessionMediaClient, {
                bigVideoUser: store.getters.bigVideoUser,
                localUser: store.getters.localUser,
                context
              })
            }
            // 如果是本地用户的媒体更改，则需要做对应的rtc操作
            context.dispatch('controlLocalMedia', controlResult)
          }
          break
        }
        case CMD.IM_MSG_RPC_RSP: // 服务器对于一些申请的返回信息
        {
          const data = packet.getData()
          const rsp = IMMsgRpcRsp.deserializeBinary(data)
          const code = rsp.getBase().getCode()
          const type = rsp.getRpctype()

          switch (type) {
            // 服务器是否允许你离开
            case EMsg.RPCType.RPC_TYPE_SIGNALING_SESSION_LEAVE: {
              if (+code === 0 && context.state.currentSession) {
                console.log('主动离开')
                // context.dispatch('leaveRtcRoom')
              }
              break
            }
          }
          break
        }
        case CMD.IM_MSG_RELAY_NTY: {
          deserializedData = IMMsgRelayNty.deserializeBinary(data)
          const msgEntity/* TMsgEntity */ = deserializedData.getMsg()
          const brief/* TMsgBrief */ = msgEntity.getBrief()
          const body = msgEntity.getBody()

          const briefMode = brief.getMode()
          const briefType = brief.getType()

          // 冻屏线条和激光笔标注点相关的信令
          if ((context.getters.whiteBoardOnUser || context.getters.freezeOnUser || context.getters.realtimeMarkOnUser) && briefMode === EMsg.MsgMode.SIGNALING_SESSION_MSG && briefType === EMsg.MsgType.MSG_TYPE_POINT_ANNOTATION_OLD) {
            const msgData = Gate.MsgData.deserializeBinary(body)
            if (msgData.getId() === MessageIds.MSG_CSYNC_NOTIFY) {
              const notifyData = CSync.Notify.deserializeBinary(msgData.getData())
              const msgList = notifyData.getMsgsList()
              if (msgList && msgList.length > 0) {
                const sData = msgList[0]
                const type = sData.getType()
                const data = sData.getData()
                // 不同的type对应不同的操作
                switch (type) {
                  case MessageIds.MSG_CSYNC_DRAWDATANOTIFY: {
                    // 有人画线了
                    const drawData = CSync.DrawDataWebNotify.deserializeBinary(data).toObject()
                    console.log('有人画线了:    ', drawData)
                    context.getters.freezeOnUser && context.commit('addLinesData', drawData)
                    context.getters.whiteBoardOnUser && context.commit('addWhiteBoardLinesData', drawData)
                    break
                  }
                  case MessageIds.MSG_CSYNC_DRAWREVOKENOTIFY: {
                    // 有人撤回了
                    const drawData = CSync.DrawRevokeNotify.deserializeBinary(data).toObject()
                    console.log('有人撤回了:    ', drawData)
                    context.getters.freezeOnUser && context.commit('deleteLinesData', drawData.controluserid)
                    context.getters.whiteBoardOnUser && context.commit('deleteWhiteBoardLinesData', drawData.controluserid)
                    break
                  }
                  case MessageIds.MSG_CSYNC_DRAWCLEARNOTIFY: {
                    // 有人清空了
                    const drawData = CSync.DrawClearNotify.deserializeBinary(data)
                    console.log('有人清空了:    ', drawData)
                    context.getters.freezeOnUser && context.commit('clearLinesData')
                    if (context.getters.whiteBoardOnUser) {
                      context.commit('clearWhiteBoardLinesData')
                      context.commit('removeWhiteBoardImage')
                    }
                    break
                  }
                  case MessageIds.MSG_CSYNC_SYNCMARKNOTIFY: {
                    // 有人激光笔标注了
                    const markData = CSync.SyncMarkNotify.deserializeBinary(data).toObject()
                    console.log('有人激光笔标注了:    ', markData)
                    context.commit('addRealtimeMark', markData)
                    break
                  }
                }
              }
            }
          }

          if (briefMode === EMsg.MsgMode.SIGNALING_SESSION_MSG && briefType === EMsg.MsgType.MSG_TYPE_POINT_ANNOTATION_OLD) {
            const bodyData = Gate.MsgData.deserializeBinary(body)
            if (bodyData.getId() === MessageIds.MSG_CHANNEL_CHANNELMSGRELAYNTY || bodyData.getId() === MessageIds.MSG_CHANNEL_CHANNELMSGSENDREQ) {
              const msgData = Entity.ChannelMsgSendReq.deserializeBinary(bodyData.getData()).toObject()
              if (msgData.channelid === context.state.currentSession.roomId) {
                let content = {}
                try {
                  content = JSON.parse(msgData.content)
                } catch (err) {
                  console.error(err)
                }
                if (content.type === 'FILE_MSG') {
                  // 上传图片
                  context.commit('coc/setNotReadFileCount')
                } else if (content.type === 'IMAGE_MSG' && context.getters.whiteBoardOnUser){
                  // 白版上传图片
                  // 清空白板，然后贴图进去
                  if (content?.text) {
                    context.dispatch('setWhiteBoardImage', content?.text)
                  }
                } else {
                  console.error('other update file type')
                }
              }
            }
          }
          break
        }
        // 你现在是房主，有其他人向你申请权限
        case CMD.CONF_ROLE_REQUEST_NTY: {
          const nty = ConfRoleRequestNty.deserializeBinary(data).toObject()
          if (nty.confid === context.state.currentSession.srcId) {
            context.commit('setSomeoneApplyRole', {
              userId: nty.requserid,
              userName: context.state.sessionMembers.find(member => +member.memberId === +nty.requserid).name,
              role: nty.role,
              roleName: Global.ROLE_ALIAS[nty.role]
            })
          }
          break
        }
        // 向房主申请的权限是否通过
        case CMD.CONF_ROLE_REQUEST_REPLY: {
          const reply = ConfRoleRequestReply.deserializeBinary(data).toObject()
          if (reply.confid === context.state.currentSession.srcId) {
            context.commit('setRoleApplicationRejected', !reply.isallow)
          }
          break
        }
      }
      break
    case MachineStatus.IncomingCall:
      switch (cmd) {
        case CMD.SIGNALING_INVITE_OFFLINE_RELAY_NTY: // 已处于被呼叫中，又收到其他用户的离线会话邀请
        case CMD.SIGNALING_INVITE_RELAY_NTY: // 已处于被呼叫中，又收到其他用户的在线会话邀请
        {
          const nty = SignalingInviteRelayNty.deserializeBinary(data)
          const roomInfo = {
            roomId: nty.getInfo().getRoomid(),
            roomKey: nty.getInfo().getRoomkey(),
            inviterId: nty.getInviterid()
          }
          if (roomInfo.roomId !== context.state.currentSession.roomId) {
            // 确认邀请
            context.dispatch('acknowledgeInvitation', roomInfo)
            // 存储邀请信息
            context.commit('saveOtherInvitations', roomInfo)
          }
          break
        }
        case CMD.SIGNALING_MEMBER_STATUS_CHANGED_NTY : // 被叫中，收到member状态变更
        {
          const nty = SignalingMemberStatusChangedNty.deserializeBinary(data).toObject()
          // 针对当前用户改变
          if (+nty.memberid === +context.state.userInfo.id) {
            // 是当前房间
            if (nty.roomid === context.state.currentSession.roomId) {
              // 当前用户被踢出
              if (nty.status === ESig.MemberStatusType.OUTROOM_USER_KICKOUT) {
                context.commit('clearCurrentSession')
                context.commit('resetCallTimeoutTimer')
                context.commit('clearSessionMediaInfo')
                context.commit('switchStatus', MachineStatus.Idle)
              }
            } else {
              const invitation = context.state.receivedOtherInvitations[nty.roomid]
              if (nty.status === ESig.MemberStatusType.PENDING_WAIT_INVITE_ACCEPT && invitation) {
                // 发送拒接请求
                context.dispatch('rejectCallAsBusy', invitation)
                context.commit('deleteOtherInvitations', invitation)
              }
            }
          }
          break
        }
        case CMD.IM_MSG_RPC_RSP: // 是否接受成功
        {
          const data = packet.getData()
          const rsp = IMMsgRpcRsp.deserializeBinary(data)
          const code = rsp.getBase().getCode()
          const type = rsp.getRpctype()
          // 接受通话的请求发送成功
          if (+code === 0 && type === EMsg.RPCType.RPC_TYPE_SIGNALING_SESSION_ACCEPT) {
            const result = SigSessionDetail.deserializeBinary(rsp.getResult())
            const detail = result && result.toObject()
            if (detail?.info?.status === ESig.SessionStatusType.SESSION_ESTABLISHED) {
              console.log('接受通话成功')
              context.commit('switchStatus', MachineStatus.SessionEstablished)
              context.dispatch('startSessionKeepAlive')
              if (detail.membersList && detail.membersList.length > 0) {
                context.commit('saveSessionMembers', transformMember(detail.membersList))
              }

              reserveCocIsMutedAlert(context, detail.membersList, detail.info.srcid)
              router.push({
                path: `/talking/${detail.info.srcid}`,
                query: {
                  from: router.currentRoute.name
                }
              })
            }
          }
          break
        }
        case CMD.SIGNALING_SESSION_STATUS_CHANGED_NTY: { // 被叫时，主叫方终止会话
          const nty = SignalingSessionStatusChangedNty.deserializeBinary(data)
          if (nty.getRoomid() === context.state.currentSession.roomId) {
            // 房间状态变更为终止，即主持人终止了此次会话
            if (nty.getStatus() === ESig.SessionStatusType.SESSION_TERMINATED) {
              context.commit('clearCurrentSession')
              context.commit('resetCallTimeoutTimer')
              context.commit('clearSessionMediaInfo')
              context.commit('switchStatus', MachineStatus.Idle)
            }
          }
          break
        }
      }
      break
    case MachineStatus.Idle:
      switch (cmd) {
        case CMD.IM_MSG_OFFLINE_MSGS_NTY: {
          // 处理离线时的消息通知
          const nty = IMOffLineMsgsNty.deserializeBinary(data)
          const msgList = nty.getMsglistList()
          // 遍历消息
          const briefList = msgList.map(msg => {
            // 处理通知相关
            handleImMsgNty(context, msg)
            const brief = msg.getBrief()
            // 为每个消息设定已收到状态
            brief.setStatus(EMsg.MsgStatus.MSG_STATUS_UNREAD)
            return brief
          })
          // 批量发送已收到消息的回执信令
          const batchRelay = new BatchIMMsgRelayRcp([
            /* base: */ [
              /* header: */generateBasePacketHeader(context),
              /* code: */0,
              /* errMsg: */''
            ]
          ])
          batchRelay.setBriefList(briefList)
          batchRelay.cmd = CMD.BATCH_IM_MSG_RELAY_RCP
          doPublish(context, batchRelay)
          break
        }
        case CMD.USER_LAST_STATUS_NTY: // 这个消息是用来广播每个人的在线状态的，任何一个人上线下线，所有其它人都会收到. hidemoxxxx是健康检查机器人，不用理会
          console.log('A USER_LAST_STATUS_NTY message')
          break
        case CMD.SIGNALING_INVITE_OFFLINE_RELAY_NTY: // 离线时被呼叫
        case CMD.SIGNALING_INVITE_RELAY_NTY: // 在线被叫入口 c++@him_sdk_plugin_signaling.cpp:onSignalingInviteRelayNty()
        {
          const nty = SignalingInviteRelayNty.deserializeBinary(data)
          context.commit('switchStatus', MachineStatus.IncomingCall)
          const membersList = nty.toObject().membersList
          const inviter = membersList.find(member => member.memberid === nty.getInviterid())
          try {
            if (Notification.permission === 'granted') { // 弹系统通知
              /* eslint-disable */
              new Notification(`${i18n.tc('mqtt.InvitesCollaboration').replace('XX', inviter.contact.name)}`)
              /* eslint-enable */
            }
          } catch (error) {
            console.log('not Notification', error.message)
          }
          // 保存来电房间信息
          context.commit('saveCurrentSession', {
            status: nty.getInfo().getStatus(),
            srcId: nty.getInfo().getSrcid(), // conference id
            roomId: nty.getInfo().getRoomid(),
            roomKey: nty.getInfo().getRoomkey(),
            roomName: nty.getInfo().getName(),
            inviterId: nty.getInviterid(),
            inviterName: inviter.contact.name,
            inviterAvatar: inviter.contact.avatarurl
          })
          // 确认邀请
          context.dispatch('acknowledgeInvitation', context.state.currentSession)
          // 超时未接听则回退至MachineStatus.Idle状态
          context.commit('saveCallTimeoutTimer', setTimeout(() => {
            console.log('超时未接听')
            context.commit('clearCurrentSession')
            context.commit('switchStatus', MachineStatus.Idle)
          }, Global.callTimeout))

          // 解析主叫方的媒体引擎
          const extInfo = nty.getInfo().getExtinfo()
          const mediaConfig = MediaEngineConfigInfo.deserializeBinary(new TextEncoder().encode(extInfo)).toObject()
          // 不是声网类型，给出提示并拒绝本次邀请
          if (mediaConfig.mediatype !== Global.MediaSource.MediaSAgora && mediaConfig.mediatype !== Global.MediaSource.MediaSLeiartc) {
            context.dispatch('rejectCallManual')
            message.error(i18n.tc('mqtt.RejectedCollaborationOfNotRTC')) // 当前协作的RTC类型不是声网或LeiaRTC，已取消本次协作
          } else {
            context.dispatch('initRtcClient', { ...mediaConfig, isHost: false })
          }
          break
        }
        case CMD.SIGNALING_JOIN_RSP: {
          // 主动加入房间的回应
          const response = SignalingJoinRsp.deserializeBinary(data)
          const base = response.getBase()
          // 不允许加入房间
          if (base.getCode() !== 0) {
            message.error(base.getErrmsg())
            router.push({
              name: PAGE_NAME.Home
            })
            return
          }
          // 如果房间异常，直接退出通话界面
          isRoomAvailable(context, response).then(async () => {
            const conferenceId = response.getInfo().getSrcid()

            reserveCocIsMutedAlert(context, response.toObject().membersList, conferenceId)
            router.push({
              path: `/talking/${conferenceId}`,
              query: {
                from: PAGE_NAME.Home
              }
            })
          }).catch(err => {
            message.error(err.message)
            context.dispatch('leaveRtcRoom')
            router.push({
              name: PAGE_NAME.Home
            })
          })
          break
        }
      }
      break
    default:
      ;// unknown status
  }
}

function doPublish(context, msg, then/* () => () */) {
  // console.log('doPublish, msg=', msg)
  if (context.rootState.mqttClient != null) {
    const packet = new Packet([
      /* cmd: */ msg.cmd,
      /* data: */ msg.serializeBinary(),
    ])
    context.rootState.mqttClient.publish(context.rootState.serverProvidedConfig.MQTTCfg.PublishTopic, packet.serializeBinary(), { qos: QoS.ExactOnce }, err => {
      if (!err) {
        console.log(`message sent： ${msg.cmd}  %c${cmdFromCode[msg.cmd]}  %c${toHump(cmdFromCode[msg.cmd])}`, 'color: blue', 'color: red')
        then && then()
      }
    })
  }
}

// 预约协作，除主持人外成员静音提醒
async function reserveCocIsMutedAlert(context, membersList, conferenceId){
  // 提示弹窗被主持人已经静音
  const localUserId = context.state.userInfo.id
  const localUserIsPresider = membersList.find(i => String(i?.contact?.id) === String(localUserId))?.contact?.rolesList.includes(1)
  const data = await store.dispatch('coc/fetchCocDetailGeneric', { id: conferenceId })
  if (!localUserIsPresider && data.mute === MUTESTATE.MUTE){
    message.warning(i18n.tc('mqtt.hostHasMutedYou'))
  }
}

const QoS = {
  AtleaseOnce: 1,
  AtmostOnce: 0,
  ExactOnce: 2,
}

// 状态机的状态，见 doc/状态图.md
const MachineStatus = {
  UnloggedIn: -1,
  Idle: 0,
  IncomingCall: 1,
  SessionEstablished: 2,
  Calling: 3,
  Rejoining: 4,
}

export default {
  onMqttMessage,
  doPublish,
  MachineStatus,
  generateBasePacketHeader,
  generateRequestId,
  QoS
}
